<script setup lang="ts">
import { ANONYMOUS_LIMITS, PLAN_COMPARISON, type ComparisonRow, type FreeAccess } from '~/constants/planComparison'
import { JOB_PACK_DAYS } from '~/constants/pricing'

/**
 * Free account vs Job Search Pack on /pricing, feature by feature, grouped
 * the way someone applying for jobs thinks: the CV, the PDF, the AI help,
 * the applications themselves and the online CV. The rows and every number
 * in them come from constants/planComparison.ts (built on the server's own
 * limits), so this file only lays them out.
 *
 * Two plan columns fit a phone, so the table never scrolls sideways and its
 * header can stick under the navbar while the long list scrolls by.
 */
const { t, locale } = useI18n()
const { isAuthenticated } = useAuthUser()
const { jobPackPrice, yearlyPrice, exportCreditPrice, price } = usePremiumPlans()

const base = 'premium.comparison'
const paidTarget = computed(() => (isAuthenticated.value ? '/admin/premium' : '/registration'))

function params(row: ComparisonRow): Record<string, string> {
  const format = new Intl.NumberFormat(locale.value)
  const numbers = Object.fromEntries(Object.entries(row.params ?? {}).map(([key, value]) => [key, format.format(value)]))
  return { ...numbers, price: exportCreditPrice }
}

function freeText(row: ComparisonRow): string | null {
  return row.freeText ? t(`${base}.rows.${row.id}.free`, params(row)) : null
}

function packText(row: ComparisonRow): string | null {
  if (row.packText) return t(`${base}.rows.${row.id}.pack`, params(row))
  return row.fairUse ? t(`${base}.unlimited`) : null
}

const marks: Record<FreeAccess, { icon: string, label: string }> = {
  yes: { icon: 'bi-check-circle-fill text-accent', label: `${base}.marks.yes` },
  limited: { icon: 'bi-check-circle text-accent', label: `${base}.marks.limited` },
  no: { icon: 'bi-x-circle text-muted dark:text-gray-500', label: `${base}.marks.no` },
}

const notes = computed(() => [
  t(`${base}.notes.fairUse`),
  t(`${base}.notes.anonymous`, { ...ANONYMOUS_LIMITS }),
  t(`${base}.notes.basic`),
  t(`${base}.notes.yearly`, { price: yearlyPrice }),
])

// Sticks under the navbar (h-11 + py-3, lg: h-12 + py-4), a pixel tucked under it.
const stickyTop = 'sticky top-[4.25rem] z-20 lg:top-[4.75rem]'
// The pack column's tint, opaque so rows scrolling under the sticky header do
// not show through: accent-soft at 40 % over white, accent at 25 % over gray-800.
const packHead = 'bg-[#D9FCE5] dark:bg-[#1A4D35]'
const cell = 'border-b border-line px-3 py-4 sm:px-5 md:px-6 dark:border-gray-700'
</script>

<template>
  <section id="comparison" class="sofax bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell">
        <SofaxSectionHeading :title="t(`${base}.title`)" :text="t(`${base}.subtitle`)" />

        <div class="mx-auto max-w-5xl rounded-[0.625rem] border border-line dark:border-gray-700">
          <table class="w-full table-fixed border-separate border-spacing-0 text-left">
            <colgroup>
              <col class="w-2/5 md:w-1/2">
              <col class="w-[30%] md:w-1/4">
              <col class="w-[30%] md:w-1/4">
            </colgroup>

            <thead>
              <tr>
                <th scope="col" :class="[cell, stickyTop]" class="rounded-tl-[0.625rem] bg-surface align-bottom dark:bg-gray-900">
                  <span class="block font-title !text-base font-semibold !text-ink md:!text-lg dark:!text-white">
                    {{ t(`${base}.feature`) }}
                  </span>
                </th>
                <th scope="col" :class="[cell, stickyTop]" class="bg-surface text-center align-bottom dark:bg-gray-900">
                  <span class="block font-title !text-base font-semibold !text-ink md:!text-lg dark:!text-white">
                    {{ t(`${base}.free.name`) }}
                  </span>
                  <span class="block font-title !text-xl font-bold !text-ink md:!text-2xl dark:!text-white">{{ price(0) }}</span>
                  <span class="block !text-xs font-normal !text-muted md:!text-sm dark:!text-gray-400">
                    {{ t(`${base}.free.note`) }}
                  </span>
                </th>
                <th scope="col" :class="[cell, stickyTop, packHead]" class="rounded-tr-[0.625rem] text-center align-bottom">
                  <span class="block font-title !text-base font-semibold !text-accent-strong md:!text-lg dark:!text-accent-soft">
                    {{ t('premium.jobPack.title') }}
                  </span>
                  <span class="block font-title !text-xl font-bold !text-ink md:!text-2xl dark:!text-white">{{ jobPackPrice }}</span>
                  <span class="block !text-xs font-normal !text-body md:!text-sm dark:!text-gray-300">
                    {{ t(`${base}.pack.note`, { days: JOB_PACK_DAYS }) }}
                  </span>
                </th>
              </tr>
            </thead>

            <tbody v-for="group in PLAN_COMPARISON" :key="group.id">
              <tr>
                <th scope="colgroup" colspan="3" :class="cell" class="bg-white pb-3 pt-8 dark:bg-gray-800">
                  <span class="flex items-center gap-2 font-title !text-sm font-bold uppercase tracking-wide !text-ink dark:!text-white">
                    <i class="bi text-base text-accent" :class="group.icon" aria-hidden="true" />
                    {{ t(`${base}.groups.${group.id}`) }}
                  </span>
                </th>
              </tr>

              <tr v-for="row in group.rows" :key="row.id" class="bg-white dark:bg-gray-800">
                <th scope="row" :class="cell" class="align-top font-normal">
                  <span class="block font-title !text-sm font-semibold !text-ink sm:!text-base dark:!text-white">
                    {{ t(`${base}.rows.${row.id}.feature`, params(row)) }}
                  </span>
                  <span class="mt-1 hidden !text-sm !text-muted sm:block dark:!text-gray-400">
                    {{ t(`${base}.rows.${row.id}.detail`, params(row)) }}
                  </span>
                </th>

                <td :class="cell" class="text-center align-top">
                  <i class="bi text-xl" :class="marks[row.free].icon" aria-hidden="true" />
                  <span class="sr-only">{{ t(marks[row.free].label) }}</span>
                  <span v-if="freeText(row)" class="mt-1 block !text-xs !text-body sm:!text-sm dark:!text-gray-300">
                    {{ freeText(row) }}
                  </span>
                </td>

                <td :class="cell" class="bg-accent-soft/15 text-center align-top dark:bg-accent/10">
                  <i class="bi bi-check-circle-fill text-xl text-accent" aria-hidden="true" />
                  <span class="sr-only">{{ t(marks.yes.label) }}</span>
                  <span v-if="packText(row)" class="mt-1 block !text-xs font-semibold !text-ink sm:!text-sm dark:!text-white">
                    {{ packText(row) }}<sup v-if="row.fairUse">*</sup>
                  </span>
                </td>
              </tr>
            </tbody>

            <tfoot class="hidden sm:table-footer-group">
              <tr>
                <td class="rounded-bl-[0.625rem] bg-surface p-3 sm:p-5 md:p-6 dark:bg-gray-900" />
                <td class="bg-surface p-3 text-center sm:p-5 md:p-6 dark:bg-gray-900">
                  <SofaxBtn to="/free-cv-builder" :label="t(`${base}.free.cta`)" variant="outline" class="btn-sm !px-4" />
                </td>
                <td :class="packHead" class="rounded-br-[0.625rem] p-3 text-center sm:p-5 md:p-6">
                  <SofaxBtn :to="paidTarget" :label="t(`${base}.pack.cta`)" variant="accent" class="btn-sm !px-4" />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Two pills do not fit the table's narrow columns on a phone -->
        <div class="mx-auto mt-6 grid max-w-5xl grid-cols-1 gap-3 sm:hidden">
          <SofaxBtn to="/free-cv-builder" :label="t(`${base}.free.cta`)" variant="outline" block />
          <SofaxBtn :to="paidTarget" :label="t(`${base}.pack.cta`)" variant="accent" block />
        </div>

        <ul class="mx-auto mt-6 grid max-w-5xl gap-2 !text-sm !text-muted dark:!text-gray-400">
          <li v-for="note in notes" :key="note">{{ note }}</li>
        </ul>
      </div>
    </div>
  </section>
</template>
